<template>
    <div class="w-full flex flex-row mb-3 xl:mb-5 align-items-center">
        <div class="block relative bg-green-200 border-circle mr-2 xl:mr-2 height-3 width-3 p-3 xl:p-4">
            <span class="absolute text-xl text-0 left-0 font-medium top-0 ml-2 mt-2 xl:ml-3 xl:mt-3">2</span>
        </div>
        <span class="flex flex-1 text-700 font-medium xl:text-xl">
            {{ content[lang_].from }} {{ content[lang_].to }} «{{ bank_title }}»
        </span>
    </div>

    <div class="w-full flex flex-column xl:flex-row relative gap-2">
        <div class="w-full flex flex-column max-w-30rem relative
            surface-0 xl:surface-100 lg:surface-100 md:surface-100 
            border-round-2xl 
            p-3 xl:px-4">
            <span v-if="card_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].card_number }}:
            </span>
            <span v-if="card_number" class="flex text-xl xl:text-3xl font-semibold text-900 mb-3 xl:mb-4">
                <span>{{ card_number }}</span>
                <span class="pi pi-copy ml-3 text-500 text-xl cursor-pointer" @click="copy(card_number)"></span>
            </span>
            <span v-if="account_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].account_number }}:
            </span>
            <span v-if="account_number" class="flex text-xl xl:text-3xl font-semibold text-900 mb-3 xl:mb-4">
                <span>{{ account_number }}</span>
                <span class="pi pi-copy ml-3 text-500 text-xl cursor-pointer" @click="copy(account_number)"></span>
            </span>
            <span v-if="fullname" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].name }}:
            </span>
            <span v-if="fullname" class="flex text-sm xl:text-lg font-medium text-900 mb-3 xl:mb-4">
                {{ fullname }}
            </span>

            <span v-if="iban_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].iban_number }}:
            </span>
            <span v-if="iban_number" class="flex text-sm xl:text-lg font-medium text-900 mb-3 xl:mb-4">
                {{ iban_number }}
            </span>

            <span v-if="upi_id" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].upi_id }}:
            </span>
            <span v-if="upi_id" class="flex text-sm xl:text-lg font-medium text-900 mb-3 xl:mb-4">
                {{ upi_id }}
            </span>

            <span v-if="telegram" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].telegram }}:
            </span>
            <span v-if="telegram" class="flex text-sm xl:text-lg font-medium text-900 mb-3 xl:mb-4">
                {{ telegram }}
            </span>

            <span v-if="amount && currency_code" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].amount }}:
            </span>
            <span v-if="amount && currency_code" class="flex text-sm xl:text-lg font-medium text-900 mb-3 xl:mb-4">
                {{ get_format_amount(amount, currency_code) }}
                <span class="pi pi-copy ml-3 text-500 text-sm cursor-pointer" @click="copyAmount(amount)"></span>
            </span>

            <span class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                {{ content[lang_].bank }}:
            </span>
            <span class="flex text-sm xl:text-lg font-medium text-900">
                {{ bank_title }}
            </span>
            <div v-if="bank_image" class="absolute flex right-0 bottom-0 surface-0 mr-3 mb-3 border-circle align-items-center justify-content-center text-center">
                <Avatar :image="bank_image" size="medium" shape="circle" class="flex" />
            </div>
        </div>

        <div class="w-full flex flex-column max-w-30rem relative py-1 xl:py-0 xl:px-8 gap-2">
            <InlineMessage v-if="currency_code=='rub'" severity="warn" class="border-round-2xl line-height-2 text-xs xl:text-md border-none" icon="pi pi-comment">
                <span class="line-height-2 text-xs xl:text-md lg:text-sm">{{ content[lang_].warn_rub }}</span>
            </InlineMessage>
            <InlineMessage severity="success" class="border-round-2xl line-height-2 text-xs xl:text-md border-none" icon="pi pi-shield">
                <span class="line-height-2 text-xs xl:text-md lg:text-sm">{{ content[lang_].message }}</span>
            </InlineMessage>
        </div>
    </div>
</template>

<script>
import Helper from '@/assets/js/Helper'
export default {
    props: [
        'amount',
        'currency_code',
        'card_number',
        'account_number',
        'fullname',
        'bank_code',
        'bank_image',
        'bank_title',
        'iban_number',
        'address',
        'city',
        'country',
        'swift_code',
        'telegram',
        'upi_id'
    ],
	data(){
		return{
			content: {
				en: {
					from: "As a payment, make a transfer from card ",
                    to: "to the card ",
                    copied: "Copied",
                    card_number: 'Card number',
                    account_number: 'Account number',
                    name: 'Recipient name',
                    amount: 'Amount',
                    iban_number: 'IBAN number',
                    telegram: 'Telegram',
                    upi_id: 'UPI ID',
                    bank: 'Recipient bank',
                    message: 'Your funds are securely protected by the PayWave secure transaction system.',
                    warn_rub: 'Do not indicate anything in the comment to the transfer.',
				} ,
				ru: {
                    from: "В качестве оплаты совершите перевод c карты любого банка ",
                    to: "на карту ",
                    copied: "Скопировано",
                    card_number: 'Номер карты',
                    account_number: 'Номер счета',
                    name: 'Имя получателя',
                    amount: 'Сумма',
                    iban_number: 'IBAN номер',
                    telegram: 'Telegram',
                    upi_id: 'UPI ID',
                    bank: 'Банк получателя',
                    message: 'Ваши средства надежно защищены системой безопасных сделок PayWave.',
                    warn_rub: 'В комментарии к переводу ничего не указывайте.',
				},
			}, 
		}
	},
	computed: {
		lang_() {
			return (this.$i18n.locale) ? this.$i18n.locale : 'ru'
		},
	},
	methods: {
        get_format_amount(value, currency) {
            let digits = 2
            if(currency=='rub' || currency=='idr') digits = 0
            return this.helper.format_amount(value, currency, digits)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: this.content[this.lang_].copied, detail: text, life: 1000 })
        },
        copyAmount(text){
            const format = this.helper.format_amount(text, null, 0)
            this.helper.copy_clipboard(format)
            this.$toast.add({ severity: 'info', summary: this.content[this.lang_].copied, detail: format, life: 1000 })
        },
	},
	beforeCreate() {
        this.helper = new Helper()    
	}
}
</script>